import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BoardCard } from '../../components/board-card/board-card';
import { BoardStateService } from '../../services/board-state.service';

@Component({
  selector: 'app-dashboard',
  imports: [BoardCard],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit {
  private router = inject(Router);
  private boardState = inject(BoardStateService);

  // Read state from centralized service
  readonly boards = this.boardState.boards;
  readonly isLoading = this.boardState.isLoading;
  readonly error = this.boardState.error;

  ngOnInit() {
    // Always refetch. The dashboard is where staleness shows: boards changed
    // in another tab, or state left over from whoever was signed in before.
    this.loadBoards();
  }

  /**
   * Load boards from API (delegates to state service)
   */
  loadBoards() {
    this.boardState.loadBoards();
  }

  /**
   * Handle copy URL action
   * Uses public_id instead of internal ID to prevent enumeration
   */
  onCopyUrl(boardId: string) {
    const board = this.boards().find((b) => b.id === boardId);
    if (board && board.public_id) {
      const url = `${window.location.origin}/display/${board.public_id}`;
      navigator.clipboard.writeText(url).then(() => {
        console.log('URL copied to clipboard:', url);
        // TODO: Show toast notification
      });
    }
  }

  /**
   * Handle edit action
   */
  onEdit(boardId: string) {
    this.router.navigate(['/boards', boardId, 'edit']);
  }

  /**
   * Navigate to create new board
   */
  createBoard() {
    this.router.navigate(['/boards/new']);
  }

  /**
   * Handle preview action
   * Navigate to board display in same tab
   * NOTE: Uses internal board ID since user is authenticated
   * The authenticated endpoint will fetch by ID (not public_id)
   */
  onPreview(boardId: string) {
    this.router.navigate(['/display', boardId]);
  }

  /**
   * Open board display in a new tab/window
   */
  onOpenInNewWindow(boardId: string) {
    window.open(`/display/${boardId}`, '_blank', 'noopener,noreferrer');
  }

  /**
   * Handle delete action
   */
  onDelete(boardId: string) {
    const board = this.boards().find((b) => b.id === boardId);
    if (!board) return;

    // Show confirmation dialog
    const confirmed = confirm(
      `Are you sure you want to delete "${board.name}"?\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      // Delete via state service
      this.boardState.deleteBoard(
        boardId,
        () => {
          console.log('Board deleted successfully');
          // TODO: Show success toast notification
        },
        (error) => {
          console.error('Failed to delete board:', error);
          alert(`Failed to delete board: ${error}`);
        }
      );
    }
  }
}
